import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  GraduationCap,
  Users,
  Backpack,
  School,
  Package,
  Tag,
  TreePalm,
  Puzzle,
  Wallet,
  NotebookPen,
  Newspaper,
  Megaphone,
  MessageSquare,
  Image as ImageIcon,
} from "lucide-react";
import { cn } from "../../lib/utils";

export const SIDEBAR_WIDTH = 208;

export const sideNavItems = [
  { key: "1", icon: LayoutDashboard, label: "Dashboard", path: "/" },
  { key: "2", icon: GraduationCap, label: "Teachers", path: "/teachers" },
  { key: "3", icon: Users, label: "Parents", path: "/parents" },
  { key: "4", icon: Backpack, label: "Children", path: "/children" },
  { key: "5", icon: School, label: "Classes", path: "/classes" },
  { key: "6", icon: Package, label: "Inventory", path: "/inventory" },
  { key: "6b", icon: Tag, label: "Categories", path: "/categories" },
  { key: "7", icon: TreePalm, label: "Holidays", path: "/holidays" },
  { key: "8", icon: Puzzle, label: "Activities", path: "/activities" },
  { key: "9", icon: Wallet, label: "Fees", path: "/fees" },
  { key: "10", icon: NotebookPen, label: "Homework", path: "/homework" },
  { key: "11", icon: Newspaper, label: "Posts", path: "/posts" },
  { key: "12", icon: MessageSquare, label: "Support", path: "/support" },
  { key: "13", icon: Megaphone, label: "Notices", path: "/notifications" },
  { key: "14", icon: ImageIcon, label: "Advertisements", path: "/advertisements" },
];

export function isNavActive(path, currentPath) {
  return path === "/" ? currentPath === "/" : currentPath.startsWith(path);
}

export function AdminSidebar({ onNavigate, className }) {
  const location = useLocation();
  const navigate = useNavigate();

  const go = (path) => {
    navigate(path);
    onNavigate?.();
  };

  return (
    <aside className={cn("admin-sidebar", className)}>
      <div className="admin-sidebar-logo">
        <img src="/images/logo_white.png" alt="Birchwood Academy" />
      </div>

      <nav className="admin-sidebar-nav">
        {sideNavItems.map((item) => {
          const Icon = item.icon;
          const active = isNavActive(item.path, location.pathname);
          return (
            <button
              key={item.key}
              type="button"
              className={cn("admin-sidebar-link", active && "is-active")}
              onClick={() => go(item.path)}
            >
              <Icon className="admin-sidebar-icon" size={16} strokeWidth={1.75} />
              <span>{item.label}</span>
            </button>
          );
        })}
      </nav>
    </aside>
  );
}

export function AdminSidebarDrawer({ open, onClose }) {
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (event) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="admin-sidebar-drawer">
      <button type="button" className="admin-sidebar-backdrop" aria-label="Close menu" onClick={onClose} />
      <AdminSidebar onNavigate={onClose} className="is-drawer" />
    </div>
  );
}

export function useIsMobileSidebar(breakpoint = 991) {
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.innerWidth <= breakpoint : false
  );

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= breakpoint);
    onResize();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [breakpoint]);

  return isMobile;
}
